import { Graphics, LINE_CAP, LINE_JOIN } from "pixi.js"
import { clamp01, easeOutCubic } from "./chapter_reward_motion"

// The shapes the chapter checklist and its celebrations are drawn from, shared so the diamond that
// flies across the screen is the same diamond that lands in the row.
//
// Palette: build_board's timber/bronze plus the ember gold the camp HUD already speaks (the print
// countdown, the tutorial clues' spark). Glows are stacked low-alpha fills on an ADD-blended
// Graphics -- no filters, see the budget-hardware note in game-ui-component-restyling.

export const REWARD_GOLD = 0xffd166
export const REWARD_GOLD_HOT = 0xffb84a
export const REWARD_EMBER = 0xff8a1f
export const REWARD_WHITE = 0xfff6d8
export const REWARD_INK_DARK = 0x2a1a0c

/** A diamond (a square stood on its corner), centred on (cx, cy), `r` from centre to tip. */
export const diamond = (g: Graphics, cx: number, cy: number, r: number) => {
    g.moveTo(cx, cy - r).lineTo(cx + r, cy).lineTo(cx, cy + r).lineTo(cx - r, cy).closePath()
}

/** The clues' four-point spark. */
export const spark = (g: Graphics, cx: number, cy: number, r: number) => {
    const k = r * 0.3
    g.moveTo(cx, cy - r)
        .lineTo(cx + k, cy - k).lineTo(cx + r, cy)
        .lineTo(cx + k, cy + k).lineTo(cx, cy + r)
        .lineTo(cx - k, cy + k).lineTo(cx - r, cy)
        .lineTo(cx - k, cy - k)
        .closePath()
}

/**
 * A check mark that draws itself: `progress` 0..1 runs along the short stroke and then the long one,
 * so a stamp can show the pen moving rather than a glyph appearing.
 */
export const check = (g: Graphics, cx: number, cy: number, r: number, progress: number, color: number,
    width: number) => {
    const p = clamp01(progress)
    if (p <= 0) return
    const a = { x: cx - r * 0.55, y: cy + r * 0.02 }
    const b = { x: cx - r * 0.12, y: cy + r * 0.45 }
    const c = { x: cx + r * 0.62, y: cy - r * 0.45 }
    const first = Math.hypot(b.x - a.x, b.y - a.y)
    const second = Math.hypot(c.x - b.x, c.y - b.y)
    const along = p * (first + second)
    g.lineStyle({ width, color, alpha: 1, cap: LINE_CAP.ROUND, join: LINE_JOIN.ROUND })
    g.moveTo(a.x, a.y)
    if (along <= first) {
        const k = along / first
        g.lineTo(a.x + (b.x - a.x) * k, a.y + (b.y - a.y) * k)
    } else {
        const k = (along - first) / second
        g.lineTo(b.x, b.y).lineTo(b.x + (c.x - b.x) * k, b.y + (c.y - b.y) * k)
    }
    g.lineStyle(0)
}

/** The gold socket of a finished task: a lit, faceted diamond with a dark check cut into it. */
export const filledSocket = (g: Graphics, cx: number, cy: number, r: number, checkProgress = 1) => {
    g.lineStyle({ width: 1, color: 0x5a3c12, alpha: 0.9, join: LINE_JOIN.MITER })
    g.beginFill(REWARD_GOLD_HOT, 1)
    diamond(g, cx, cy, r)
    g.endFill()
    g.lineStyle(0)
    // The upper facets catch the light.
    g.beginFill(REWARD_GOLD, 1)
    g.moveTo(cx, cy - r + 1).lineTo(cx + r - 1, cy).lineTo(cx, cy + 0.5).lineTo(cx - r + 1, cy).closePath()
    g.endFill()
    g.beginFill(REWARD_WHITE, 0.75)
    g.moveTo(cx, cy - r + 1.5).lineTo(cx - r * 0.5, cy - r * 0.5 + 0.5).lineTo(cx, cy - 0.5).closePath()
    g.endFill()
    check(g, cx, cy, r * 0.82, checkProgress, REWARD_INK_DARK, Math.max(1.4, r * 0.3))
}

/** Concentric low-alpha discs: a soft radial glow on an ADD layer. */
export const glowDisc = (g: Graphics, cx: number, cy: number, r: number, color: number, alpha: number,
    bands = 4) => {
    if (!(alpha > 0) || !(r > 0)) return
    for (let i = 0; i < bands; i++) {
        const k = 1 - i / bands
        g.beginFill(color, alpha / bands).drawCircle(cx, cy, r * k).endFill()
    }
}

/** A ring of short sparks thrown out from a point; `t` 0..1 through their flight. */
export const sparkBurst = (g: Graphics, cx: number, cy: number, t: number, count: number, from: number,
    to: number, color: number, seed = 0, width = 1.5) => {
    if (!(t > 0) || t >= 1) return
    const travel = easeOutCubic(t)
    const alpha = 1 - t * t
    for (let i = 0; i < count; i++) {
        const angle = (i / count) * Math.PI * 2 + seed + (i % 2 ? 0.18 : -0.1)
        const reach = (i % 3 === 0 ? 1 : i % 3 === 1 ? 0.78 : 0.9)
        const r1 = from + (to - from) * travel * reach
        const tail = Math.max(2, (to - from) * 0.28 * (1 - t))
        const r0 = Math.max(from, r1 - tail)
        g.lineStyle({ width, color, alpha, cap: LINE_CAP.ROUND })
        g.moveTo(cx + Math.cos(angle) * r0, cy + Math.sin(angle) * r0)
            .lineTo(cx + Math.cos(angle) * r1, cy + Math.sin(angle) * r1)
    }
    g.lineStyle(0)
}

/**
 * A slanted band of light crossing the box (x, y, w, h), clipped to it by hand -- a mask per frame
 * would cost a stencil pass for a 0.7s glint. `t` 0..1 carries it from fully left of the box to fully
 * right of it.
 */
export const shineBand = (g: Graphics, x: number, y: number, w: number, h: number, t: number,
    band: number, alpha: number, color = REWARD_WHITE) => {
    if (t < 0 || t > 1 || !(alpha > 0)) return
    const slant = h * 0.6
    const start = x - band - slant + (w + band + slant) * t
    // Nested passes, widest faintest, so the band has a soft falloff rather than a hard-edged slab.
    for (const [width, a] of [[band, alpha * 0.22], [band * 0.72, alpha * 0.26], [band * 0.46, alpha * 0.3],
        [band * 0.22, alpha * 0.35]] as const) {
        const left = start + (band - width) / 2
        const poly = clipToBox([
            { x: left + slant, y }, { x: left + slant + width, y },
            { x: left + width, y: y + h }, { x: left, y: y + h },
        ], x, x + w)
        if (poly.length < 3) continue
        g.beginFill(color, a)
        g.drawPolygon(poly.flatMap(p => [p.x, p.y]))
        g.endFill()
    }
}

/** Sutherland-Hodgman against two vertical edges: all a slanted band ever needs. */
export const clipToBox = (points: { x: number; y: number }[], minX: number, maxX: number) => {
    const clip = (input: { x: number; y: number }[], inside: (p: { x: number }) => boolean, edgeX: number) => {
        const out: { x: number; y: number }[] = []
        for (let i = 0; i < input.length; i++) {
            const cur = input[i]
            const prev = input[(i + input.length - 1) % input.length]
            const cross = () => {
                const k = (edgeX - prev.x) / (cur.x - prev.x)
                return { x: edgeX, y: prev.y + (cur.y - prev.y) * k }
            }
            if (inside(cur)) {
                if (!inside(prev)) out.push(cross())
                out.push(cur)
            } else if (inside(prev)) out.push(cross())
        }
        return out
    }
    return clip(clip(points, p => p.x >= minX, minX), p => p.x <= maxX, maxX)
}
